import {
    useCallback, useMemo, useReducer, useState,
} from 'react'
import {
    useJsApiLoader, Libraries,
} from '@react-google-maps/api'
import clsx from 'clsx'
import isEqual from 'lodash/isEqual'

import useFieldsWithClassName from 'app/hooks/useFieldsWithClassName'
import useQuery from 'app/hooks/useQuery'
import useGetMainCategory from 'app/hooks/useGetMainCategory'
import useEnumValues from 'app/hooks/useEnumValues'
import getAllowedCategoriesTypes from 'app/Apps/ContactManagement/utils/getAllowedCategoriesTypes'
import useGetAllowedCategoryType from 'app/Apps/ContactManagement/utils/useGetAllowedCategoryType'
import reducer from 'app/Apps/ContactManagement/store/reducer'
import {
    ArchiveButton, ArchiveConfirmationDialog, useArchive,
} from 'app/shared-components/Archive'
import {
    setValue,
} from 'app/Apps/ContactManagement/store/actions'
import Button from 'app/shared-components/Button'
import ButtonContainer from 'app/shared-components/ButtonContainer'
import useHasPermission from 'app/hooks/useHasPermission'
import {
    Library,
} from 'app/types/googlemaps.types'
import {
    AddressCategory, ContactType, LocationType,
} from 'app/enums/label.enums'
import DomainObjectForm from 'app/shared-components/DomainObjectForm'
import {
    generateSkyNetLink,
} from 'app/SkyNetRoutes'

import useContactManagementRoutes from 'app/SkyNetRoutes/hooks/useContactManagementRoutes'
import locationRequests from 'app/requests/Location.requests'
import locationAccessRights from 'app/access-rights/locationAccessRights'

import {
    Location,
    ROLES_PROSPECT_ALLOWED, ROLES_NOPROSPECT_AND_PROSPECT_ALLOWED,
} from '../Locations.types'
import LocationDuplicationCheckDialog from '../LocationDuplicationCheckDialog'
import useLocationDuplicationCheck from '../hooks/useLocationDuplicationCheck'
import useHasLocationPermissionType from '../../utils/useHasLocationPermissionType'
import useStyles from './LocationForm.styles'
import getLocationFormFields from './LocationForm.fields'

type LocationFormProps = {
    data?: Location,
    action: (...args: any[]) => void,
    onCancel: (...args: any[]) => void,
    isCompanyFieldDisabled?: boolean,
}

export const defaultProps: Partial<LocationFormProps> = {
    data: {
        locationType: LocationType.ADDRESS,
        enhancedSkyCenterScanning: true,
    },
    isCompanyFieldDisabled: false,
}

const libraries: Libraries = [Library.PLACES]

const {
    REACT_APP_GOOGLE_MAP_KEY: googleMapKey,
} = process.env

const getGridWrapperClass = (value: Location, isNew: boolean) => {
    if ((!value.isShippingLocation || value.isProspect)
        && value.locationType === LocationType.ADDRESS) {
        return 'gridWrapperAddressWithoutShipping'
    }
    if (isNew && value.locationType === LocationType.ADDRESS) {
        return 'gridWrapperNew'
    }

    switch (value.locationType) {
    case LocationType.ADDRESS:
        return 'gridWrapperAddress'
    case LocationType.AIRPORT:
        return 'gridWrapperAirport'
    case LocationType.SEAPORT:
        return 'gridWrapperSeaport'
    case LocationType.RAILPORT:
        return 'gridWrapperRailport'
    default:
        return 'gridWrapperAddress'
    }
}

const LocationForm = ({
    data,
    action,
    onCancel,
    isCompanyFieldDisabled,
}: LocationFormProps) => {
    const {
        Locations: LocationsRoutes,
    } = useContactManagementRoutes()
    const {
        isLoaded: isGoogleMapsServiceLoaded,
    } = useJsApiLoader({
        googleMapsApiKey: googleMapKey,
        libraries,
    })

    const isNew = !data?.id

    const [
        duplicationData,
        setDuplicationData,
    ] = useState(null)

    const getAllowedValues = useEnumValues()

    const queryParams = useQuery([
        'companyId',
        'locationType',
    ])

    const canEditProspect = useHasPermission(ROLES_PROSPECT_ALLOWED) && data?.isProspect

    const hasProspectChangePermission = useHasPermission(ROLES_NOPROSPECT_AND_PROSPECT_ALLOWED)

    const initialData = useMemo(() => {
        const companyId = queryParams?.companyId
        const locationType = queryParams?.locationType
        const valuesCompanyId = companyId ? {
            ...data,
            companyId,
        } : data

        return locationType ? {
            ...valuesCompanyId, locationType,
        } : valuesCompanyId
    }, [
        queryParams?.companyId,
        queryParams?.locationType,
        data,
    ])

    const [
        {
            value,
            timeZones,
        },
        dispatch,
    ]: [
        { value: Location, timeZones: [] },
        (args: { type: string, value: Location }) => void,
    ] = useReducer(reducer, {
        value: initialData,
        edited: false,
        timeZones: [],
    })

    const mainCategories = useGetMainCategory()

    const addressMainCategories = useGetAllowedCategoryType(locationAccessRights, isNew ? 'create' : 'edit')

    const hasReadPermissions = useHasLocationPermissionType(value.addressMainCategory)
    const hasCategoriesEditPermissions = useHasLocationPermissionType(value.addressMainCategory, 'edit')

    const isNotEditable = useMemo(() => {
        return !(canEditProspect
                || isNew
                || Boolean(
                    hasCategoriesEditPermissions
                    && value.addressMainCategory
                    && value.addressMainCategory.length,
                ))
            || value.locationType === LocationType.AIRPORT
    }, [
        canEditProspect,
        hasCategoriesEditPermissions,
        value.addressMainCategory,
        value.locationType,
        isNew,
    ])

    const allowedCategories = useMemo(() => {
        if (isNotEditable) {
            return getAllowedValues({
                ...AddressCategory, ...ContactType,
            })
        }
        if (mainCategories) {
            return getAllowedCategoriesTypes(
                mainCategories?.addressMainCategory,
                addressMainCategories,
                true,
                getAllowedValues,
            )
        }
        return []
    }, [
        isNotEditable,
        mainCategories,
        addressMainCategories,
        getAllowedValues,
    ])

    const {
        timeZone,
        currentPlaceId,
    } = value
    const {
        classes,
    } = useStyles()

    const setAutocomplete = useCallback((val: Location, isAddressLine1 = false) => {
        const updatedValue = isAddressLine1
            ? {
                ...value,
                addressLine1: val,
            }
            : {
                ...value,
                ...val,
            }

        if (!isEqual(updatedValue, value)) {
            dispatch(setValue(updatedValue))
        }
    }, [value])

    const fields = useMemo(() => {
        return getLocationFormFields(
            {
                country: value?.country,
                locationType: value.locationType,
                isShippingLocation: value.isShippingLocation,
                isCompanyDisabled: isCompanyFieldDisabled,
                timeZones,
                categories: value.addressCategory,
                isNew,
                allowedCategories,
                setAutocomplete,
                timeZone,
                currentPlaceId,
                value,
                isGoogleMapsServiceLoaded,
                getAllowedValues,
                isProspect: value?.isProspect,
                orignalIsProspect: data?.isProspect,
                isProspectDisabled: !hasProspectChangePermission,
            },
        )
    }, [
        value,
        isCompanyFieldDisabled,
        timeZones,
        isNew,
        allowedCategories,
        setAutocomplete,
        timeZone,
        currentPlaceId,
        isGoogleMapsServiceLoaded,
        getAllowedValues,
        data?.isProspect,
        hasProspectChangePermission,
    ])

    const fieldsWithClassName = useFieldsWithClassName(fields, classes)

    const onChangeValue = useCallback((val: Location) => {
        if (!isEqual(val, value)) {
            dispatch(setValue(val))
        }
    }, [value])

    const isNotReadable = useMemo(() => {
        return !hasReadPermissions && !isNew
            && value.addressMainCategory
            && value.addressMainCategory.length
    }, [
        hasReadPermissions,
        value.addressMainCategory,
        isNew,
    ])

    const archive = useArchive({
        requestParamFn: data?.archived ? locationRequests.restore
            : locationRequests.archive,
        notificationId: 'LocationUpdateDto',
        title: 'Location',
        data,
        redirectionAfterSuccess: generateSkyNetLink({
            domainPath: LocationsRoutes.path,
        }),
    })

    const onCheckDuplicateSuccess = useCallback((duplicates) => {
        if (!duplicates?.length) {
            action(value)
            return
        }

        setDuplicationData(duplicates)
    }, [
        setDuplicationData,
        action,
        value,
    ])

    const checkDuplicate = useLocationDuplicationCheck(onCheckDuplicateSuccess)

    const callAction = useCallback(() => {
        if (value?.isShippingLocation && value?.locationType === LocationType.ADDRESS) {
            checkDuplicate({
                longitude: value?.longitude,
                latitude: value?.latitude,
                editedLocation: data.id ? {
                    id: data.id,
                } : undefined,
            })

            return
        }

        let result = value

        if (value.isProspect) {
            result = {
                ...value,
                isShippingLocation: false,
                longitude: undefined,
                latitude: undefined,
            }
        }

        action(result)
    }, [
        checkDuplicate,
        action,
        value,
        data.id,
    ])

    const onCancelClick = useCallback(() => {
        dispatch(setValue({
            ...initialData,
            placeId: null,
        }))
    }, [initialData])

    const closeConfirmationDialog = useCallback(() => {
        setDuplicationData(null)
    }, [setDuplicationData])

    const classNames = useMemo(() => {
        return {
            gridWrapper: classes[getGridWrapperClass(value, isNew)],
        }
    }, [
        classes,
        isNew,
        value,
    ])

    if (isNotReadable) {
        return null
    }
    return (
        <div
            className={classes.root}
        >
            <DomainObjectForm
                name="Location"
                value={value}
                fields={fieldsWithClassName}
                className={clsx(classNames.gridWrapper)}
                disabled={isNotEditable}
                exists={!isNew}
                onChange={onChangeValue}
            />
            {value.locationType !== LocationType.AIRPORT && (
                <ButtonContainer>
                    <Button
                        label={isNew ? 'Create' : 'Save'}
                        onClick={callAction}
                        disabled={Boolean(duplicationData?.length) || isNotEditable}
                    />
                    {isNew ? null : (
                        <ArchiveButton
                            onClick={archive.handleArchiveRestore}
                            archived={data?.archived}
                            disabled={isNotEditable}
                        />
                    )}
                    <Button
                        disabled={Boolean(duplicationData?.length)}
                        secondary
                        label="Cancel"
                        onClick={onCancel || onCancelClick}
                    />
                </ButtonContainer>
            )}
            <ArchiveConfirmationDialog
                title="Location"
                openConfirmDialog={archive.openConfirmDialog}
                handleClose={archive.handleArchiveRestoreConfirm}
                archived={data?.archived}
            />
            <LocationDuplicationCheckDialog
                createLocation={() => {
                    return action(value)
                }}
                data={duplicationData}
                close={closeConfirmationDialog}
                title={!isNew ? 'Update Confirmation' : undefined}
                positiveLabel={!isNew ? 'Save anyway' : undefined}
            />
        </div>
    )
}

LocationForm.defaultProps = defaultProps

export default LocationForm
